// useDocumentProcessing.ts - Generic document processing for any upload target
import {
  type AIChatSession,
  type CustomerCase as Case,
  type Document,
  DocumentsService,
  DocumentTypeEnum,
  type SessionGroup,
  UploadOriginEnum
} from '@/types/generated'
import { WebSocketMessageType } from '@/utils/channels/types'
import { computed, type ComputedRef, readonly, ref, type Ref, watch } from 'vue'
import {
  createOrganizationDocumentHandler,
  useOrganizationDocumentProcessing
} from './useOrganizationDocumentProcessing'

export interface DocumentStatus {
  uuid: string
  id?: string // For uploads in progress

  // Document data (from server)
  document?: Document

  // Upload info (for active uploads)
  uploadInfo?: {
    id: string
    filename: string
    size: number
    progress: number
    targetId: string // Generic target ID (case UUID, session UUID, etc.)
    started: number
    error?: string
    documentUuid?: string // Link upload to document UUID when known
  }

  // Display properties
  filename: string
  progress: number

  // Status flags - derived from document data
  isUploading: boolean
  isProcessing: boolean
  isComplete: boolean
  hasError: boolean
  errorMessage?: string
}

export type UploadTarget = {
  type: 'case' | 'session' | 'sessionGroup'
  id: string
  data?: any // Optional data for the target (e.g., case data, session data)
}

export function useDocumentProcessing(uploadTarget: Ref<UploadTarget | null>) {
  // SINGLE SOURCE OF TRUTH - All document states managed here
  const documents = ref<DocumentStatus[]>([])

  // Callback for when data should be refreshed
  let onRefreshNeeded: ((documentUuid?: string) => Promise<void>) | null = null

  // Set the refresh callback
  function setRefreshCallback(callback: (documentUuid?: string) => Promise<void>) {
    onRefreshNeeded = callback
  }

  // Helper function to update document state based on processing status
  // This is shared between document-specific and case channel handlers
  function updateDocumentStatusByMessage(documentUuid: string, status: string, error?: string) {
    const doc = documents.value.find((d) => d.uuid === documentUuid)
    if (!doc) return

    if (status === 'STARTED') {
      doc.isProcessing = true
      doc.isComplete = false
      doc.hasError = false
      doc.progress = 95
    } else if (status === 'FINISHED') {
      doc.isProcessing = false
      doc.isComplete = true
      doc.hasError = false
      doc.progress = 100

      // Trigger reactivity by reassigning the array
      documents.value = [...documents.value]

      if (onRefreshNeeded) {
        onRefreshNeeded(documentUuid)
      }
    } else if (status === 'FAILED') {
      doc.isProcessing = false
      doc.isComplete = false
      doc.hasError = true
      doc.errorMessage = error || 'Processing failed'
      doc.progress = 0
      if (onRefreshNeeded) {
        onRefreshNeeded(documentUuid)
      }
    }
  }

  // Conditionally create organization document handler and websocket
  // Only for session and sessionGroup types, not for cases
  const organizationDocumentProcessing = ref<ReturnType<
    typeof useOrganizationDocumentProcessing
  > | null>(null)

  // Watch for target changes and manage organization websocket lifecycle
  watch(
    uploadTarget,
    (newTarget) => {
      const shouldUse = newTarget?.type === 'session' || newTarget?.type === 'sessionGroup'

      if (shouldUse && !organizationDocumentProcessing.value) {
        // Create organization document handler and websocket
        const organizationDocumentHandler = createOrganizationDocumentHandler(
          updateDocumentStatusByMessage
        )
        organizationDocumentProcessing.value = useOrganizationDocumentProcessing(
          organizationDocumentHandler
        )
      } else if (!shouldUse && organizationDocumentProcessing.value) {
        // Stop and clear organization websocket
        organizationDocumentProcessing.value.stopSubscription()
        organizationDocumentProcessing.value = null
      }
    },
    { immediate: true }
  )

  // Handle document completion and trigger refresh if needed
  async function handleDocumentCompletion(documentUuid: string) {
    // Delay the refresh slightly to allow backend processing to complete
    setTimeout(async () => {
      if (onRefreshNeeded) {
        try {
          await onRefreshNeeded(documentUuid)
        } catch (error) {
          console.error('Error refreshing data:', error)
        }
      }
    }, 2000) // 2 second delay to allow backend to finish processing
  }

  // Initialize and sync with target data
  watch(
    uploadTarget,
    (newTarget, oldTarget) => {
      // If target changed (different session/case), preserve in-progress documents
      // Don't clear them - WebSocket events might still arrive for them
      if (oldTarget && newTarget && oldTarget.id !== newTarget.id) {
        // Only remove documents that are complete and not for this target
        // Keep processing documents and uploads in progress
        documents.value = documents.value.filter(
          (doc) =>
            doc.isProcessing ||
            doc.isUploading ||
            (doc.isComplete && !doc.hasError && doc.uploadInfo?.targetId === newTarget.id)
        )
      }

      if (newTarget?.data?.documents && Array.isArray(newTarget.data.documents)) {
        syncWithTargetDocuments(newTarget.data.documents)
      } else {
        // Only clear if no target at all - but preserve in-progress docs
        if (!newTarget) {
          documents.value = documents.value.filter((doc) => doc.isProcessing || doc.isUploading)
        }
      }
    },
    { immediate: true, deep: true }
  )

  // Sync documents from target with our state management
  function syncWithTargetDocuments(targetDocuments: Document[]) {
    // Create a map of existing documents to preserve states
    const existingDocs = new Map<string, DocumentStatus>()
    documents.value.forEach((doc) => {
      if (doc.uuid) existingDocs.set(doc.uuid, doc)
    })

    // Process target documents from server
    const newDocuments: DocumentStatus[] = []
    const processedUuids = new Set<string>()

    targetDocuments.forEach((doc) => {
      if (!doc.uuid) return

      const existing = existingDocs.get(doc.uuid)
      const docStatus = createDocumentStatus(doc, existing)
      newDocuments.push(docStatus)
      processedUuids.add(doc.uuid)
    })

    // CRITICAL: Preserve documents that are processing/uploading but not in server response yet
    // This ensures WebSocket events can still find and update them
    documents.value.forEach((doc) => {
      // Skip if already processed from server
      if (processedUuids.has(doc.uuid)) return

      // Preserve if:
      // 1. Still uploading (no document UUID yet)
      // 2. Still processing (WebSocket might update it)
      // 3. Has upload info but document not created yet
      if (doc.isUploading || doc.isProcessing || (doc.uploadInfo && !doc.uploadInfo.documentUuid)) {
        newDocuments.push(doc)
      }
    })

    // Update documents list
    documents.value = sortDocuments(newDocuments)
  }

  // Create document status from Document data
  function createDocumentStatus(doc: Document, existing?: DocumentStatus): DocumentStatus {
    // CRITICAL: If existing document was just marked complete via websocket (FINISHED),
    // preserve that state even if server hasn't updated yet
    if (existing && existing.isComplete && !existing.hasError && doc.uuid === existing.uuid) {
      // Document was just marked complete via websocket - preserve that state
      return {
        ...existing,
        document: doc, // Update document data but keep processing flags
        filename: doc.filename || doc.title || existing.filename
      }
    }

    // Fixed processing detection logic
    // A document is processing if:
    // 1. It's uploaded but not finished yet AND no error
    // 2. OR it has started processing but not finished
    const isProcessing = Boolean(
      (!doc.metadata_enrichment_is_finished && !doc.metadata_enrichment_error) ||
        (doc.metadata_enrichment_started_at &&
          !doc.metadata_enrichment_is_finished &&
          !doc.metadata_enrichment_error)
    )

    const isComplete = Boolean(doc.metadata_enrichment_is_finished)
    const hasError = Boolean(doc.metadata_enrichment_error)

    // Check if document just completed (was processing, now complete)
    if (existing && existing.isProcessing && isComplete && !hasError && doc.uuid) {
      handleDocumentCompletion(doc.uuid)
    }

    return {
      uuid: doc.uuid!,
      document: doc,
      filename: doc.filename || doc.title || 'Unknown',
      progress: existing?.progress || (isProcessing ? 95 : 100),

      // Status flags - preserve existing state if it was just updated via websocket
      isUploading: existing?.isUploading || false,
      isProcessing: existing && existing.isComplete ? false : isProcessing,
      isComplete: existing?.isComplete || isComplete,
      hasError: existing?.hasError || hasError,
      errorMessage: hasError ? doc.metadata_enrichment_error || undefined : existing?.errorMessage,

      // Preserve upload info if it exists and is relevant
      uploadInfo: existing?.uploadInfo?.documentUuid === doc.uuid ? existing.uploadInfo : undefined
    }
  }

  // Sort documents by priority and timestamp
  function sortDocuments(docs: DocumentStatus[]): DocumentStatus[] {
    return docs.sort((a, b) => {
      // Priority: failed > in-progress (uploading/processing) > complete

      // 1. Failed documents first
      if (a.hasError && !b.hasError) return -1
      if (!a.hasError && b.hasError) return 1

      // 2. In-progress documents (uploading or processing) second
      const aInProgress = a.isUploading || a.isProcessing
      const bInProgress = b.isUploading || b.isProcessing
      if (aInProgress && !bInProgress) return -1
      if (!aInProgress && bInProgress) return 1

      // 3. Within in-progress, prioritize uploading over processing
      if (a.isUploading && !b.isUploading) return -1
      if (!a.isUploading && b.isUploading) return 1

      // 4. Then by timestamp (newest first)
      const aTime = a.uploadInfo?.started || 0
      const bTime = b.uploadInfo?.started || 0
      return bTime - aTime
    })
  }

  // === UPLOAD MANAGEMENT ===

  function registerUpload(targetId: string, filename: string, size: number): string {
    const uploadId = `${targetId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    const documentStatus: DocumentStatus = {
      uuid: uploadId,
      id: uploadId,
      filename,
      isUploading: true,
      isProcessing: false,
      isComplete: false,
      hasError: false,
      progress: 0,
      uploadInfo: {
        id: uploadId,
        filename,
        size,
        progress: 0,
        targetId,
        started: Date.now()
      }
    }

    documents.value.push(documentStatus)
    return uploadId
  }

  function updateUploadProgress(uploadId: string, progress: number) {
    const doc = documents.value.find((d) => d.id === uploadId)
    if (doc) {
      doc.progress = progress
      if (doc.uploadInfo) {
        doc.uploadInfo.progress = progress
      }
    }
  }

  function setUploadError(uploadId: string, error: string) {
    const doc = documents.value.find((d) => d.id === uploadId)
    if (doc) {
      doc.isUploading = false
      doc.isProcessing = false
      doc.isComplete = false
      doc.hasError = true
      doc.errorMessage = error
      if (doc.uploadInfo) {
        doc.uploadInfo.error = error
      }
    }
  }

  function completeUpload(uploadId: string, documentUuid: string, document: Document) {
    const doc = documents.value.find((d) => d.id === uploadId)
    if (doc) {
      doc.uuid = documentUuid
      doc.document = document
      doc.isUploading = false
      doc.isProcessing = true
      doc.isComplete = false
      doc.hasError = false
      doc.progress = 100
      if (doc.uploadInfo) {
        doc.uploadInfo.documentUuid = documentUuid
        doc.uploadInfo.progress = 100
      }

      // Processing tracking is now handled by organization websocket
      // No need for individual document subscriptions
    }
  }

  function removeUpload(uploadId: string) {
    const index = documents.value.findIndex((d) => d.id === uploadId)
    if (index > -1) {
      documents.value.splice(index, 1)
    }
  }

  // === UPLOAD FUNCTIONS ===

  async function uploadDocument(file: File): Promise<Document> {
    const target = uploadTarget.value
    if (!target) {
      throw new Error('No upload target specified')
    }

    const uploadId = registerUpload(target.id, file.name, file.size)

    try {
      updateUploadProgress(uploadId, 0)

      let response: Document

      switch (target.type) {
        case 'case':
          response = await DocumentsService.documentsUploadCreate({
            caseUuid: target.id,
            formData: {
              file: file,
              upload_origin: UploadOriginEnum.MANUELL_HINZUGEFÜGT
            }
          })
          break

        case 'session':
          response = await DocumentsService.documentsChatSessionUploadCreate({
            sessionUuid: target.id,
            formData: {
              file: file,
              upload_origin: UploadOriginEnum.AI_GLOBAL_CHAT
            }
          })
          break

        case 'sessionGroup':
          response = await DocumentsService.documentsChatSessionGroupUploadCreate({
            groupUuid: target.id,
            formData: {
              file: file,
              upload_origin: UploadOriginEnum.AI_GLOBAL_CHAT
            }
          })
          break

        default:
          throw new Error(`Unsupported upload target type: ${target.type}`)
      }

      updateUploadProgress(uploadId, 100)

      if (response.uuid) {
        completeUpload(uploadId, response.uuid, response)
      }

      return response
    } catch (error: any) {
      const errorMessage = error?.body?.file?.[0] || error?.body?.detail || 'Upload failed'
      setUploadError(uploadId, errorMessage)
      throw error
    }
  }

  // === RETRY FUNCTIONALITY ===

  async function retryDocumentProcessing(documentUuid: string): Promise<boolean> {
    try {
      await DocumentsService.documentsRetryProcessingCreate({
        uuid: documentUuid,
        requestBody: {}
      })

      // Update document status immediately to show processing state
      const docIndex = documents.value.findIndex((d) => d.uuid === documentUuid)
      if (docIndex >= 0) {
        documents.value[docIndex].isProcessing = true
        documents.value[docIndex].hasError = false
        documents.value[docIndex].errorMessage = undefined
        documents.value[docIndex].progress = 95 // Show as processing

        // Processing tracking is now handled by organization websocket
        // No need for individual document subscriptions
      }

      return true
    } catch (error) {
      console.error('Error retrying document processing:', error)
      return false
    }
  }

  // === CASE CHANNEL HANDLER ===

  /**
   * Handle case document processing messages from case channel.
   * This is needed for case documents which receive updates via case channel too.
   *
   * Note: This function does NOT create a subscription - it handles messages from
   * an existing case channel subscription managed by the case store.
   *
   * The case store watches its caseWebSocketSubscription and calls this function
   * when CASE_DOCUMENT_PROCESSING messages arrive.
   */
  function handleCaseDocumentProcessingMessage(message: any) {
    if (!message || message.type !== WebSocketMessageType.CASE_DOCUMENT_PROCESSING) return

    const data = message.data
    if (!data || !data.document_uuid) return

    // Update document state using shared helper
    updateDocumentStatusByMessage(data.document_uuid, data.status, data.error)

    // Processing tracking is now handled by organization websocket
    // No need for individual document subscriptions
  }

  // === COMPUTED PROPERTIES ===

  const processingDocuments = computed(() => documents.value.filter((doc) => doc.isProcessing))

  const completedDocuments = computed(() =>
    documents.value.filter((doc) => doc.isComplete && !doc.hasError)
  )

  const documentsWithErrors = computed(() => documents.value.filter((doc) => doc.hasError))

  const activeUploads = computed(() => documents.value.filter((doc) => doc.isUploading))

  const usableDocumentsCount = computed(
    () => documents.value.filter((doc) => doc.isComplete && !doc.hasError).length
  )

  const hasActiveUploads = computed(() => activeUploads.value.length > 0)

  const hasProcessingDocuments = computed(() => processingDocuments.value.length > 0)

  // === CLEANUP ===

  function clearDocumentProcessingStates(): void {
    // Stop organization websocket subscription if it exists
    if (organizationDocumentProcessing.value) {
      organizationDocumentProcessing.value.stopSubscription()
      organizationDocumentProcessing.value = null
    }

    documents.value = []
  }

  return {
    // Single source of truth
    documents: documents,

    // Computed properties
    processingDocuments,
    completedDocuments,
    documentsWithErrors,
    activeUploads,
    usableDocumentsCount,
    hasActiveUploads,
    hasProcessingDocuments,

    // Upload functions
    registerUpload,
    updateUploadProgress,
    setUploadError,
    completeUpload,
    removeUpload,
    uploadDocument,

    // Document tracking
    clearDocumentProcessingStates,

    // Retry functionality
    retryDocumentProcessing,

    // Refresh functionality
    setRefreshCallback,

    // Case channel handler
    handleCaseDocumentProcessingMessage,

    // Organization websocket state
    organizationWebsocket: computed(() => {
      if (!organizationDocumentProcessing.value) {
        return {
          isConnected: readonly(ref(false)),
          isConnecting: readonly(ref(false)),
          hasError: readonly(ref(false)),
          error: readonly(ref(null)),
          reconnect: () => {}
        }
      }
      return {
        isConnected: organizationDocumentProcessing.value.isConnected,
        isConnecting: organizationDocumentProcessing.value.isConnecting,
        hasError: organizationDocumentProcessing.value.hasError,
        error: organizationDocumentProcessing.value.error,
        reconnect: organizationDocumentProcessing.value.reconnect
      }
    })
  }
}

/**
 * Simplified composable for case document processing.
 *
 * This composable wraps useDocumentProcessing and automatically handles
 * the conversion from a case ref/computed to the upload target format.
 *
 * :param caseRef: A ref or computed ref containing the case data
 * :return: Document processing interface with all standard methods
 */
export function useCaseDocumentProcessing(caseRef: Ref<Case | null> | ComputedRef<Case | null>) {
  const uploadTarget = computed(() => {
    const caseRecord = caseRef.value
    return caseRecord?.uuid
      ? {
          type: 'case' as const,
          id: caseRecord.uuid,
          data: caseRecord
        }
      : null
  })

  return useDocumentProcessing(uploadTarget)
}

/**
 * Simplified composable for chat session document processing.
 *
 * This composable wraps useDocumentProcessing and automatically handles
 * the conversion from a chat session ref/computed to the upload target format.
 *
 * :param sessionRef: A ref or computed ref containing the chat session data
 * :return: Document processing interface with all standard methods
 */
export function useChatSessionDocumentProcessing(
  sessionRef: Ref<AIChatSession | null> | ComputedRef<AIChatSession | null>
) {
  const uploadTarget = computed(() => {
    const session = sessionRef.value
    return session?.uuid
      ? {
          type: 'session' as const,
          id: session.uuid,
          data: session
        }
      : null
  })

  return useDocumentProcessing(uploadTarget)
}

/**
 * Simplified composable for chat session group document processing.
 *
 * This composable wraps useDocumentProcessing and automatically handles
 * the conversion from a session group ref/computed to the upload target format.
 *
 * :param groupRef: A ref or computed ref containing the session group data
 * :return: Document processing interface with all standard methods
 */
export function useChatSessionGroupDocumentProcessing(
  groupRef: Ref<SessionGroup | null> | ComputedRef<SessionGroup | null>
) {
  const uploadTarget = computed(() => {
    const group = groupRef.value
    return group?.uuid
      ? {
          type: 'sessionGroup' as const,
          id: group.uuid,
          data: group
        }
      : null
  })

  return useDocumentProcessing(uploadTarget)
}

export const isDocumentReadOnly = (doc: Document): boolean => {
  if (!doc) return false

  return (
    doc.document_type === DocumentTypeEnum.KI_GENERIERTES_DOKUMENT ||
    doc.document_type === DocumentTypeEnum.KI_CHAT ||
    doc.document_type === DocumentTypeEnum.KI_DOKUMENTENGENERATOR_VORLAGE ||
    doc.document_type === DocumentTypeEnum.ANFRAGE ||
    doc.document_type === DocumentTypeEnum.E_MAIL ||
    doc.document_type === DocumentTypeEnum.TRANSKRIPT
  )
}
